import React, { useContext, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useAuth } from 'common/providers/useAuth'
import { SocialButtons } from './SocialButtons'
import { FooterText } from './FooterText'
import { FormPageWrapper } from './FormPageWrapper'
import { FormMessage } from './FormMessage'
import { AuthPageContext } from '../../providers/AuthPageProvider'
import { encodeAuthState } from 'common/utils/auth'
import { InputField, PasswordField } from 'cwp-components/forms'
import { CheckboxGroup } from '../Inputs/CheckboxGroup'
import { Button } from 'common/components/Authentication/Button'

const SignUpPage = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError: setFieldError,
    trigger,
    watch,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })
  const [isChecked, setIsChecked] = useState<boolean>(false)
  const [repeatPasswordError, setRepeatPasswordError] = useState<
    string | undefined
  >('Please confirm your password')

  const handleChange = () => {
    setIsChecked(!isChecked)
  }
  const { hasSocialSignIn, accountType, hasMarketing } =
    useContext(AuthPageContext)

  const [error, setError] = useState<string | undefined>()
  const { signUp } = useAuth()

  const onSubmit = async (values) => {
    const fieldNames = Object.keys(errors)
    await trigger(fieldNames)

    signUp(
      {
        ...values,
        account_type: accountType,
        accepted_marketing: hasMarketing,
      },
      {
        onSuccess: () => {
          document.location = `${
            document.location.origin
          }/verify-email?state=${encodeAuthState({
            email: values.emailAddress,
            password: values.password,
          })}`
        },
        onError: (error: string) => {
          if (error === 'An account with the given email already exists.') {
            setFieldError('emailAddress', { message: error })
          } else {
            setError(error || 'Something went wrong')
          }
        },
      },
    )
  }

  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
  const alphaPattern = /^[A-Za-z]+$/

  const password = watch('password')
  const repeatPassword = watch('repeatPassword')

  return (
    <FormPageWrapper className={'w-full md:w-[84%] '} title='Create Account'>
      <FormMessage type='error'>{error}</FormMessage>
      <form onSubmit={handleSubmit(onSubmit)} className='mt-10'>
        <div className='grid md:grid-cols-3 gap-4'>
          <div className='flex flex-col col-span-3 item-center'>
            <InputField
              register={register}
              required={true}
              regexPattern={alphaPattern}
              id='firstName'
              label='First name'
              placeholder='First Name'
              errors={errors}
              errorMessage='Please enter your first name'
            />
          </div>
          <div className='flex flex-col col-span-3 item-center'>
            <InputField
              register={register}
              required={true}
              regexPattern={alphaPattern}
              id='lastName'
              label='Last name'
              placeholder='Last Name'
              errors={errors}
              errorMessage='Please enter your last name'
            />
          </div>
          <div className='flex flex-col col-span-3 item-center'>
            <InputField
              register={register}
              required={true}
              regexPattern={emailPattern}
              id='emailAddress'
              label='Email address'
              placeholder='Email address'
              errors={errors}
              errorMessage='Please enter a valid email address'
            />
          </div>
          <div className='flex flex-col col-span-3 item-center'>
            <PasswordField
              register={register}
              required={true}
              id='password'
              label='Password'
              placeholder='Password'
              isLabelVisible
              errors={errors}
              errorMessage='Please set your password'
            />
          </div>
          <div className='flex flex-col col-span-3 item-center'>
            <PasswordField
              register={register}
              required={true}
              password={password}
              repeatPassword={repeatPassword}
              id='repeatPassword'
              label='Confirm password'
              placeholder='Confirm Password'
              isLabelVisible
              errors={errors}
              errorMessage={
                errors.repeatPassword?.message || repeatPasswordError
              }
            />
          </div>
        </div>
        <div className='w-full mx-auto'>
          <CheckboxGroup
            id='terms'
            className='my-1 flex flex-row items-start'
            label={
              <span className='text-base text-black'>
                I agree with the
                <a
                  href={'/terms-of-use-consumer'}
                  className='!text-[#37A2BA] fw-bold inline-block px-1'
                >
                  Terms
                </a>
                and
                <a
                  href='/privacy-policy-web'
                  className='!text-[#37A2BA] inline-block px-1'
                >
                  Privacy policy
                </a>
              </span>
            }
            checked={isChecked}
            onChange={handleChange}
          />
        </div>
        <Button
          type='submit'
          className='!w-[104px] mt-10 !mx-auto'
          disabled={Object.keys(errors).length > 0 || !isChecked}
        >
          Continue
        </Button>
      </form>

      <div className='mt-4 '>
        {hasSocialSignIn && (
          <>
            <p className='text-base font-normal text-center text-primary-font !py-4 mb-4 !leading-6'>
              or
            </p>
            <SocialButtons className={'w-full mx-auto'} action='Sign up' />
          </>
        )}
        <FooterText
          text='Already have an account?'
          linkHref='/login'
          linkText='Sign In'
        />
      </div>
    </FormPageWrapper>
  )
}

export default SignUpPage
